import { create, fromBinary, toBinary } from "@bufbuild/protobuf";
import type { DescMessage, MessageInitShape, MessageShape } from "@bufbuild/protobuf";
import { isRecord } from "../app/typeGuards";
import {
  AuthResponseSchema,
  CatalogSchema,
  CreateRoomRequestSchema,
  ErrorResponseSchema,
  GuestRequestSchema,
  JoinResponseSchema,
  JoinRoomRequestSchema,
  LoadoutsResponseSchema,
  LoginRequestSchema,
  MeSchema,
  NetInfoSchema,
  QuickplayRequestSchema,
  RankedQueueRequestSchema,
  RankedStatusSchema,
  RegisterRequestSchema,
  RoomListSchema,
  SelectLoadoutRequestSchema,
  UpdateMeRequestSchema,
} from "../proto/gunrun/v1/api_pb";
import type { NetInfo, RoomInfo } from "../proto/gunrun/v1/api_pb";
import { LoadoutSchema } from "../proto/gunrun/v1/common_pb";
import type { GameMode, Loadout } from "../proto/gunrun/v1/common_pb";
import {
  completeAuth,
  completeCatalog,
  completeJoin,
  completeLoadout,
  completeMe,
  completeRankedStatus,
} from "./complete";
import { ApiError } from "./errors";
import type {
  CreateRoomInit,
  FullAuthResponse,
  FullCatalog,
  FullJoin,
  FullLoadout,
  FullMe,
  FullRankedStatus,
} from "./types";

export { ApiError, errorMessage, isApiError } from "./errors";

/** The REST API speaks binary protobuf (docs/ARCHITECTURE.md "API contracts"). */
const PROTOBUF = "application/x-protobuf";
const DEFAULT_TIMEOUT_MS = 15_000;

let authToken: string | null = null;
let unauthorizedHandler: (() => void) | null = null;

/** Bearer token attached to every request that has `auth` enabled. */
export function setAuthToken(token: string | null): void {
  authToken = token;
}

/**
 * Called when an authenticated request fails with 401 (expired or revoked session).
 * Not called for the auth endpoints themselves (wrong password is a normal 401).
 */
export function setUnauthorizedHandler(handler: (() => void) | null): void {
  unauthorizedHandler = handler;
}

interface RequestOptions {
  /** Encoded request message. */
  body?: Uint8Array<ArrayBuffer>;
  /** Send the bearer token when one is set. Default true. */
  auth?: boolean;
  /** Invoke the unauthorized handler on 401. Default true. */
  handle401?: boolean;
  signal?: AbortSignal;
  timeoutMs?: number;
}

/** Encode a request from a message or a plain init object (a message must be complete: nested plain objects are not accepted). */
function encode<D extends DescMessage>(schema: D, init: MessageInitShape<D>): Uint8Array<ArrayBuffer> {
  return toBinary(schema, create(schema, init));
}

/** Read the `ErrorResponse` of a failed response; null when the body is not one (proxy error pages). */
async function readError(res: Response): Promise<{ code: string; message: string } | null> {
  const type = res.headers.get("content-type") ?? "";
  try {
    if (type.includes("json")) {
      const data: unknown = await res.json();
      if (isRecord(data) && typeof data.error === "string" && data.error) {
        return { code: typeof data.code === "string" ? data.code : "", message: data.error };
      }
      return null;
    }
    if (type !== "" && !type.includes("protobuf")) return null;
    const err = fromBinary(ErrorResponseSchema, new Uint8Array(await res.arrayBuffer()));
    return err.error ? { code: err.code, message: err.error } : null;
  } catch {
    return null;
  }
}

/** Perform a request and return the raw success body (empty for 204). */
async function send(method: string, path: string, opts: RequestOptions): Promise<Uint8Array> {
  const headers: Record<string, string> = { Accept: PROTOBUF };
  if ((opts.auth ?? true) && authToken) headers.Authorization = `Bearer ${authToken}`;
  if (opts.body) headers["Content-Type"] = PROTOBUF;
  const timeout = AbortSignal.timeout(opts.timeoutMs ?? DEFAULT_TIMEOUT_MS);
  const signal = opts.signal ? AbortSignal.any([opts.signal, timeout]) : timeout;

  let res: Response;
  try {
    res = await fetch(path, { method, headers, body: opts.body, signal });
  } catch (e) {
    if (opts.signal?.aborted) throw e; // caller cancelled: let AbortError propagate
    if (e instanceof DOMException && e.name === "TimeoutError") {
      throw new ApiError(0, "timeout", "The server took too long to respond");
    }
    throw new ApiError(0, "network", "Cannot reach the server");
  }

  if (!res.ok) {
    const err = await readError(res);
    if (res.status === 401 && (opts.handle401 ?? true) && authToken) unauthorizedHandler?.();
    throw new ApiError(
      res.status,
      err?.code || `http_${res.status}`,
      err?.message || res.statusText || `Request failed (${res.status})`,
    );
  }
  if (res.status === 204) return new Uint8Array(0);
  return new Uint8Array(await res.arrayBuffer());
}

/** Request with a typed response message. */
async function call<D extends DescMessage>(
  method: string,
  path: string,
  response: D,
  opts: RequestOptions = {},
): Promise<MessageShape<D>> {
  const bytes = await send(method, path, opts);
  try {
    return fromBinary(response, bytes);
  } catch {
    throw new ApiError(0, "bad_response", "The server sent an invalid response");
  }
}

// ---- Auth ----

export async function guestLogin(displayName?: string): Promise<FullAuthResponse> {
  const body = encode(GuestRequestSchema, { displayName: displayName || undefined });
  return completeAuth(await call("POST", "/api/auth/guest", AuthResponseSchema, { body, auth: false, handle401: false }));
}

/** With a guest token set, the guest account is claimed and progress is kept. */
export async function register(req: {
  username: string;
  password: string;
  displayName?: string;
}): Promise<FullAuthResponse> {
  const body = encode(RegisterRequestSchema, req);
  return completeAuth(await call("POST", "/api/auth/register", AuthResponseSchema, { body, handle401: false }));
}

export async function login(username: string, password: string): Promise<FullAuthResponse> {
  const body = encode(LoginRequestSchema, { username, password });
  return completeAuth(await call("POST", "/api/auth/login", AuthResponseSchema, { body, auth: false, handle401: false }));
}

export async function logout(): Promise<void> {
  await send("POST", "/api/auth/logout", { handle401: false });
}

// ---- Account ----

export async function getMe(signal?: AbortSignal): Promise<FullMe> {
  return completeMe(await call("GET", "/api/me", MeSchema, { signal }));
}

export async function patchMe(displayName: string): Promise<FullMe> {
  const body = encode(UpdateMeRequestSchema, { displayName });
  return completeMe(await call("PATCH", "/api/me", MeSchema, { body }));
}

export async function getMeta(signal?: AbortSignal): Promise<FullCatalog> {
  return completeCatalog(await call("GET", "/api/meta", CatalogSchema, { signal }));
}

// ---- Loadouts ----

/** Index 0..4. 400 `invalid_loadout` / 403 `locked_item` come back as ApiError. Returns all five slots. */
export async function putLoadout(index: number, loadout: Loadout): Promise<FullLoadout[]> {
  const body = encode(LoadoutSchema, loadout);
  const res = await call("PUT", `/api/loadouts/${index}`, LoadoutsResponseSchema, { body });
  return res.loadouts.map(completeLoadout);
}

export async function selectLoadout(index: number): Promise<void> {
  await send("PUT", "/api/loadouts/selected", { body: encode(SelectLoadoutRequestSchema, { index }) });
}

// ---- Rooms ----

export async function listRooms(signal?: AbortSignal): Promise<RoomInfo[]> {
  const res = await call("GET", "/api/rooms", RoomListSchema, { signal });
  return res.rooms;
}

export async function createRoom(req: CreateRoomInit): Promise<FullJoin> {
  const body = encode(CreateRoomRequestSchema, req);
  return completeJoin(await call("POST", "/api/rooms", JoinResponseSchema, { body }));
}

export async function joinRoom(id: string, password?: string): Promise<FullJoin> {
  const body = encode(JoinRoomRequestSchema, { password: password || undefined });
  return completeJoin(await call("POST", `/api/rooms/${encodeURIComponent(id)}/join`, JoinResponseSchema, { body }));
}

// ---- Matchmaking ----

export async function quickplay(modes: readonly GameMode[]): Promise<FullJoin> {
  const body = encode(QuickplayRequestSchema, { modes: [...modes] });
  return completeJoin(await call("POST", "/api/matchmaking/quickplay", JoinResponseSchema, { body }));
}

export async function startRanked(mode: GameMode): Promise<FullRankedStatus> {
  const body = encode(RankedQueueRequestSchema, { mode });
  return completeRankedStatus(await call("POST", "/api/matchmaking/ranked", RankedStatusSchema, { body }));
}

export async function getRankedStatus(signal?: AbortSignal): Promise<FullRankedStatus> {
  return completeRankedStatus(await call("GET", "/api/matchmaking/ranked", RankedStatusSchema, { signal }));
}

export async function cancelRanked(): Promise<void> {
  await send("DELETE", "/api/matchmaking/ranked", {});
}

// ---- Net ----

export function getNetInfo(signal?: AbortSignal): Promise<NetInfo> {
  return call("GET", "/api/net", NetInfoSchema, { signal });
}
